import { Link } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { matchesQuery } from '../recipes'
import type { Recipe } from '../recipes'
import { authorName } from '../sisters'
import { Modal } from '../shell/Modal'
import type { MembersData } from './useMembersData'

/**
 * The search box from the top bar, opened over the page. Ctrl K opens it too.
 *
 * It reads the two lists already loaded and nothing else: recipes by title, ingredient and
 * sister, prayers by their words.
 */
export function SearchModal({
  data,
  onClose,
  onRecipe,
}: {
  data: MembersData
  onClose: () => void
  onRecipe: (recipe: Recipe) => void
}) {
  const [query, setQuery] = useState('')
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    input.current?.focus()
  }, [])

  const hits = useMemo(() => {
    const q = query.trim()
    if (!q) return { recipes: [] as Recipe[], prayers: [] as NonNullable<MembersData['board']> }
    return {
      recipes: (data.recipes ?? []).filter((r) => matchesQuery(r, q)).slice(0, 6),
      prayers: (data.board ?? []).filter((p) => p.body.toLowerCase().includes(q.toLowerCase())).slice(0, 6),
    }
  }, [query, data])

  const empty = query.trim() && hits.recipes.length === 0 && hits.prayers.length === 0

  return (
    <Modal title="Search" icon="ph-magnifying-glass" top onClose={onClose}>
      <input
        ref={input}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="A recipe, an ingredient, a sister, a word from a prayer"
        aria-label="Search"
      />
      <div className="search-hits">
        {!query.trim() && (
          <>
            <div className="well-label">Try</div>
            {['Sabbath', 'bread', 'honey'].map((word) => (
              <button key={word} className="search-hit" type="button" onClick={() => setQuery(word)}>
                <span>
                  <i className="ph-bold ph-magnifying-glass" aria-hidden="true" /> {word}
                </span>
              </button>
            ))}
          </>
        )}
        {hits.recipes.map((recipe) => (
          <button
            key={recipe.id}
            className="search-hit"
            type="button"
            onClick={() => {
              onRecipe(recipe)
              onClose()
            }}
          >
            <b>{recipe.title}</b>
            <span className="where">Recipes</span>
          </button>
        ))}
        {hits.prayers.map((entry) => (
          <Link key={entry.id} className="search-hit" to="/sisters" onClick={onClose}>
            <b>{entry.body.length > 90 ? `${entry.body.slice(0, 90)}…` : entry.body}</b>
            <span className="where">{authorName(entry)}</span>
          </Link>
        ))}
        {empty && <p className="quiet-center">Nothing matches that.</p>}
      </div>
    </Modal>
  )
}
